import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type GetLandingPageResult, type ListEmbedsResult } from '@whinerp/client';
import { BLOCK_PALETTES, type Block, type BlockDocument } from '@whinerp/contracts/blocks';
import { formatDateTime } from '@whinerp/contracts/format';
import { BlockEditor, Button, ErrorState, PageHeading, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { blockEditorLabels } from './block-labels.js';
import { useBlockLibrary } from './block-library.js';
import { defineScreen } from '../types.js';

/**
 * SC-63 Landing page builder (F-072).
 *
 * The same block editor an email uses, with the landing palette, and the page
 * settings a hosted page needs. Publish is not offered until the preview of
 * the version on screen has been opened - "Preview URL before publish,
 * always" - and the server refuses it either way.
 */

type Page = GetLandingPageResult;
const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

function LandingBuilderScreen(): JSX.Element {
  const t = useT();
  const library = useBlockLibrary();
  const errorText = useErrorMessage();
  const format = useFormat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams();
  const creating = (params['id'] ?? 'new') === 'new';
  const pageId = creating ? null : (params['id'] ?? null);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [formEmbedId, setFormEmbedId] = useState('');
  const [document, setDocument] = useState<BlockDocument>({ blocks: [] });
  const [previewPath, setPreviewPath] = useState<string | null>(null);

  const page = useQuery<Page, ApiError>({
    queryKey: ['landing-page', pageId],
    queryFn: () => api.getLandingPage({ path: { pageId: pageId ?? '' } }),
    enabled: pageId !== null,
  });
  const forms = useQuery<ListEmbedsResult, ApiError>({ queryKey: ['embeds'], queryFn: () => api.listEmbeds({}) });

  useEffect(() => {
    const data = page.data;
    if (!data) return;
    setName(data.name);
    setSlug(data.slug);
    setTitle(data.title);
    setDescription(data.description ?? '');
    setFormEmbedId(data.formEmbedId ?? '');
    setDocument({ blocks: data.blocks as Block[] });
  }, [page.data]);

  const refresh = async (saved: Page) => {
    queryClient.setQueryData(['landing-page', saved.pageId], saved);
    await queryClient.invalidateQueries({ queryKey: ['landing-pages'] });
  };
  const save = useMutation<Page, ApiError>({
    mutationFn: () =>
      api.saveLandingPage({
        body: {
          pageId,
          name,
          slug,
          title,
          description: description.trim() === '' ? null : description,
          formEmbedId: formEmbedId || null,
          blocks: document.blocks,
        },
      }),
    onSuccess: async (saved) => {
      setPreviewPath(null);
      await refresh(saved);
      if (creating) navigate(`/landing-pages/${saved.pageId}`, { replace: true });
    },
  });
  const preview = useMutation<{ path: string }, ApiError>({
    mutationFn: () => api.landingPreviewLink({ path: { pageId: pageId ?? '' }, body: {} }),
    onSuccess: (link) => setPreviewPath(link.path),
  });
  const publish = useMutation<Page, ApiError>({
    mutationFn: () => api.publishLandingPage({ path: { pageId: pageId ?? '' }, body: {} }),
    onSuccess: refresh,
  });
  const unpublish = useMutation<Page, ApiError>({
    mutationFn: () => api.unpublishLandingPage({ path: { pageId: pageId ?? '' }, body: {} }),
    onSuccess: refresh,
  });

  if (pageId !== null && page.isPending) return <Skeleton className="h-plot w-full" />;
  if (page.isError) return <ErrorState title={t('state.error')} body={errorText(page.error)} onRetry={() => void page.refetch()} />;

  const current = page.data;
  const previewed = current !== undefined && current.previewedVersion === current.draftVersion;
  const refusal = save.error ?? preview.error ?? publish.error ?? unpublish.error;
  const captureForms = (forms.data?.embeds ?? []).filter((embed) => embed.widget === 'form' && !embed.revokedAt);

  return (
    <div data-component="LandingBuilder" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-center gap-3">
        <PageHeading>{creating ? t('landing.new') : name}</PageHeading>
        {current?.publicPath ? (
          <a data-landing-live href={current.publicPath} className="text-meta text-bronze-text hover:text-ink">
            {t('landing.liveAt', { values: { path: current.publicPath, date: formatDateTime(current.publishedAt ?? '', format) } })}
          </a>
        ) : null}
        <span className="ms-auto flex flex-wrap gap-2">
          <Button intent="secondary" data-landing-save disabled={save.isPending} onClick={() => save.mutate()}>
            {t('landing.saveDraft')}
          </Button>
          {current ? (
            <Button intent="secondary" data-landing-preview-link disabled={preview.isPending} onClick={() => preview.mutate()}>
              {t('landing.makePreview')}
            </Button>
          ) : null}
          {current ? (
            <Button
              intent="primary"
              data-landing-publish
              disabled={!previewed || publish.isPending}
              title={previewed ? undefined : t('landing.previewFirst')}
              onClick={() => publish.mutate()}
            >
              {t('landing.publish')}
            </Button>
          ) : null}
          {current?.publicPath ? (
            <Button intent="ghost" data-landing-unpublish disabled={unpublish.isPending} onClick={() => unpublish.mutate()}>
              {t('landing.unpublish')}
            </Button>
          ) : null}
        </span>
      </header>
      {refusal ? (
        <p data-landing-builder-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
          {errorText(refusal)}
        </p>
      ) : null}
      {current && !previewed ? (
        <p data-landing-needs-preview className="text-meta text-ink-meta">{t('landing.previewFirst')}</p>
      ) : null}
      {previewPath ? (
        <p className="text-dense text-ink">
          {t('landing.previewReady')}{' '}
          <Link data-landing-preview-open to={previewPath} target="_blank" className="text-bronze-text hover:text-ink">
            {t('landing.openPreview')}
          </Link>
        </p>
      ) : null}

      <Panel data-region="landing-settings">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('landing.name')}
            <input data-landing-name className={INPUT} value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('landing.slug')}
            <input data-landing-slug className={INPUT} value={slug} onChange={(event) => setSlug(event.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('landing.title')}
            <input data-landing-title className={INPUT} value={title} onChange={(event) => setTitle(event.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('landing.form')}
            <select data-landing-form-select className={INPUT} value={formEmbedId} onChange={(event) => setFormEmbedId(event.target.value)}>
              <option value="">{t('landing.noForm')}</option>
              {captureForms.map((embed) => (
                <option key={embed.embedId} value={embed.embedId}>{embed.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600 sm:col-span-2">
            {t('landing.description')}
            <input data-landing-description className={INPUT} value={description} onChange={(event) => setDescription(event.target.value)} />
          </label>
        </div>
      </Panel>

      <Panel data-region="landing-content">
        <div className="flex flex-col gap-2">
          <SectionLabel>{t('landing.content')}</SectionLabel>
          <BlockEditor palette={BLOCK_PALETTES['landing-page']} value={document} onChange={setDocument} labels={blockEditorLabels(t)} library={library} />
        </div>
      </Panel>
    </div>
  );
}

function BuilderLoading(): JSX.Element {
  return <Skeleton className="h-plot w-full" />;
}
function BuilderError(): JSX.Element {
  const t = useT();
  return <ErrorState title={t('state.error')} body={t('landing.missing')} />;
}
function BuilderEmpty(): JSX.Element {
  const t = useT();
  return <p className="text-body text-ink-meta">{t('landing.emptyBuilder')}</p>;
}

export const screen = defineScreen({
  id: 'SC-63',
  route: '/landing-pages/:id',
  surface: 'page',
  title: 'Landing page builder',
  features: ['F-072'],
  entitlement: 'F-072',
  reachedBy: [{ kind: 'screen', from: 'SC-16', how: 'a landing page on the campaigns list' }],
  components: [],
  elements: ['LandingBuilder', 'BlockEditor'],
  sampleRecord: [{ table: { name: 'landing_page', idColumn: 'page_id' }, param: 'id' }],
  states: { empty: BuilderEmpty, loading: BuilderLoading, error: BuilderError },
  component: LandingBuilderScreen,
});

export default screen;
